<template>
  <NewStudioModalsModalShell :title="shellTitle" data-figma-node="2850:169004" :class="`start-modal start-modal--${view}`" @close="$emit('close')">
    <template v-if="view === 'start'">
      <p class="intro">Get started by choosing how you’d like to create your space:</p>
      <div class="start-choices">
        <button class="start-choice start-choice--customize" type="button" @click="view = 'gallery'">
          <span class="choice-art"><img src="/new-studio/icons/start-customize.svg" alt=""></span>
          <strong>Customize a Design</strong><small>Customize a premade design.</small>
        </button>
        <b>OR</b>
        <button class="start-choice start-choice--scratch" type="button" @click="view = 'patterns'">
          <span class="choice-art"><img src="/new-studio/icons/start-scratch.svg" alt=""></span>
          <strong>Create from Scratch</strong><small>Start a new design from scratch.</small>
        </button>
      </div>
    </template>
    <NewStudioModalsGalleryDesignsModal v-else-if="view === 'gallery'" embedded @back="view = 'start'" @close="$emit('close')" />
    <template v-else>
      <p class="intro">Choose a grid for your new design.</p>
      <div class="pattern-grid">
        <button
          v-for="pattern in GRID_PATTERN_LIST"
          :key="pattern.id"
          type="button"
          :class="{ active: selectedPattern?.id === pattern.id }"
          @pointerdown="selectPatternOnPointerDown(pattern, $event)"
          @click="selectedPattern = pattern"
        >
          <img :src="pattern.image" alt=""><span>{{ pattern.label }}</span>
        </button>
      </div>
      <p v-if="showGridWarning" class="warning">Changing the grid will reset the Studio. Save any work you want to keep.</p>
      <button class="primary" type="button" :disabled="!selectedPattern" @click="handleStart">{{ showGridWarning ? 'Continue Without Saving' : 'Start Designing' }}</button>

      <!-- Grid change warning modal -->
      <Teleport to="body">
        <transition name="fade">
          <div v-if="showWarningModal" class="grid-warning-overlay" @click.self="showWarningModal = false">
            <div class="grid-warning-modal" role="alertdialog" aria-label="Grid change warning">
              <div class="grid-warning-modal__content">
                <p>Changing the grid will reset the Studio. Save any work you want to keep.</p>
                <div class="grid-warning-modal__actions">
                  <button type="button" class="grid-warning-modal__cancel" @click="showWarningModal = false">Cancel</button>
                  <button type="button" class="grid-warning-modal__confirm" @click="confirmStart">Continue</button>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </Teleport>
    </template>
  </NewStudioModalsModalShell>
</template>

<script setup lang="ts">
import { GRID_PATTERN_LIST, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { GRID_MENU } from '~/composables/shapes/constants'
import { ModalType } from '~/stores/modal'
import type { GridPattern } from '~/types/grid.types'
import { applyGridPatternChange } from '../logic'

defineEmits<{ close: [] }>()
const gridStore = useGridStore()
const projectStore = useProjectStore()
const modalStore = useModalStore()
const options = computed(() => modalStore.getModalOptions(ModalType.New))
const view = ref(options.value?.activeContent === 'patternList' ? 'patterns' : 'start')
const shellTitle = computed(() => view.value === 'gallery' ? "Felt Right's Gallery Designs" : 'Start Designing')
const selectedPattern = ref<GridPattern | null>(null)
const showGridWarning = computed(() => Boolean(options.value?.showGridWarning))
const selectPatternOnPointerDown = (pattern: GridPattern, event: PointerEvent) => {
  if (event.button === 0) selectedPattern.value = pattern
}
const showWarningModal = ref(false)
const handleStart = () => {
  if (!selectedPattern.value) return
  if (gridStore.shapeList.length > 0) {
    showWarningModal.value = true
    return
  }
  startFromScratch()
}
const confirmStart = () => {
  showWarningModal.value = false
  startFromScratch()
}
const startFromScratch = () => {
  if (!selectedPattern.value) return
  const defaultShape = GRID_MENU[selectedPattern.value.id]?.default
  if (!defaultShape) return
  const gridRef = projectStore.gridRef
  applyGridPatternChange(gridStore, selectedPattern.value, { preserveDimensions: showGridWarning.value })
  projectStore.$reset()
  projectStore.setGridRef(gridRef)
  useRouter().replace({ query: {} })
  deleteCookie('studio_design_id')
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
  gridStore.setSelectedShape(defaultShape)
  gridStore.setTempItemByCurrentSelection()
  modalStore.handleSuccess(ModalType.New)
}
</script>

<style scoped>
.intro { margin: 3px 0 37.25px; text-align: center; color: #55554f; font-size: 16px; font-weight: 600; }
.start-choices { position: relative; left: .765625px; display: grid; grid-template-columns: 304.75px 19px 304.77px; align-items: center; justify-content: center; gap: 32.5px; padding-top: 0; }
.start-choices button { display: flex; flex-direction: column; align-items: center; border: 0; background: transparent; color: #1c1c1e; cursor: pointer; }
.start-choices b { padding-top: 4px; text-align: center; font-size: 19px; font-weight: 800; }
.choice-art { display: grid; width: 100%; height: 210px; margin-bottom: 20px; padding: 22.75px 32px; place-items: center; border: 2px solid #ece9e3; border-radius: 8px; background: #f6f5f2; }
.start-choice--customize .choice-art img { width: 240px; height: 135.29px; object-fit: contain; }
.start-choice--scratch .choice-art img { width: 240.75px; height: 160.5px; object-fit: cover; object-position: 50% 50%; }
.start-choices strong { font-size: 23px; font-weight: 800; letter-spacing: -.23px; }
.start-choices small { margin-top: 7px; color: #7c7b76; font-size: 14.5px; font-weight: 500; }
:global(.start-modal--start > h2) { margin-top: 17px; }
:global(.start-modal--start) { height: 491.5px; min-height: 491.5px; max-height: 491.5px; }
:global(.start-modal--gallery) { height: 732px; min-height: 732px; max-height: 732px; overflow-y: auto; }
:global(.start-modal--gallery > h2) { margin-top: 20px; }
.pattern-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
.pattern-grid button { padding: 9px; border: 1px solid #deddd9; background: #f8f7f4; font: inherit; cursor: pointer; touch-action: manipulation; }
.pattern-grid button:hover { border-color: #b8c6ce; background: #dfe4e5; }
.pattern-grid button.active { border-color: #2f365d; background: #ccd5da; box-shadow: inset 0 0 0 2px #2f365d; }
.pattern-grid img { width: 100%; aspect-ratio: 1; object-fit: cover; }
.pattern-grid span { display: block; margin-top: 6px; font-size: 11px; font-weight: 700; }
.warning { color: #8b4b13; font-size: 13px; }
.primary { width: 100%; min-height: 52px; margin-top: 18px; border: 0; background: #2e3b48; color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.primary:disabled { opacity: .4; cursor: not-allowed; }
.grid-warning-overlay { position: fixed; z-index: 10000; inset: 0; display: grid; place-items: center; background: rgb(28 28 30 / 28%); backdrop-filter: blur(4px); }
.grid-warning-modal { width: min(440px, calc(100vw - 40px)); padding: 32px 40px; border-radius: 12px; background: #fff; box-shadow: 0 8px 32px rgb(0 0 0 / 18%); }
.grid-warning-modal__content p { margin: 0 0 24px; text-align: center; color: #55554f; font-size: 17px; font-weight: 600; line-height: 1.5; }
.grid-warning-modal__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.grid-warning-modal__cancel, .grid-warning-modal__confirm { min-height: 48px; border-radius: 6px; font: inherit; font-size: 16px; font-weight: 700; cursor: pointer; }
.grid-warning-modal__cancel { border: 1px solid #d9d8d3; background: #fff; color: #55554f; }
.grid-warning-modal__confirm { border: 0; background: #2e3a46; color: #fff; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
